import { Button, Card, Progress, Space, Typography, theme } from 'antd';
import React from 'react';

import { useStepGenerating } from '@/hooks/useStepGenerating';
import { SparkleIcon } from '@/icons';
import { GENERATION_PHASE_STEPS } from '@/utils/assessmentInterviewFlow';

export interface StepGeneratingProps {
  stepNumber: 1 | 2 | 3 | 4;
  /** Scoped assessment flow: calls generate API then navigates via this callback. */
  assessmentInstanceId?: string;
  accessToken?: string | null;
  clientId?: string;
  projectId?: string;
  onScopedSuccess?: () => void;
  /** When generation fails (toast is shown here). */
  onScopedFailure?: () => void;
  /** Legacy: manual navigation to the output page when no assessment instance is present. */
  onOpenOutput?: () => void;
  clientLabel?: string;
}

const { Text, Title } = Typography;

export const StepGenerating: React.FC<StepGeneratingProps> = ({
  stepNumber,
  assessmentInstanceId,
  accessToken = null,
  clientId,
  projectId,
  onScopedSuccess,
  onScopedFailure,
  onOpenOutput,
  clientLabel,
}) => {
  const { token } = theme.useToken();

  const isScoped = Boolean(
    assessmentInstanceId &&
    onScopedSuccess &&
    accessToken &&
    clientId &&
    projectId &&
    stepNumber === 1,
  );

  const { currentPhase, liveProgress, runScopedGeneration } = useStepGenerating(
    isScoped,
    assessmentInstanceId,
    accessToken,
    clientId,
    projectId,
    onScopedSuccess,
    onScopedFailure,
  );

  if (isScoped) {
    return (
      <div className="step-generating__centered">
        <div className="step-generating__icon-ring">
          <SparkleIcon style={{ fontSize: 32, color: token.colorPrimary }} />
        </div>
        <Title level={3}>Generating Interview Guide</Title>
        <Text type="secondary" className="step-generating__subtitle">
          Blend AI is analyzing your context and building role-specific interview frameworks
          {clientLabel ? ` for ${clientLabel}` : ' for your engagement'}.
        </Text>
        <Progress
          percent={liveProgress}
          status="active"
          strokeColor={token.colorPrimary}
          showInfo={false}
          style={{ maxWidth: 400, width: '100%' }}
        />
        {currentPhase !== null && (
          <Text type="secondary" className="step-generating__phase-label">
            {GENERATION_PHASE_STEPS.find((s) => s.phase === currentPhase)?.label ?? ''}
          </Text>
        )}
        <Button type="link" onClick={() => void runScopedGeneration(true)}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={4} style={{ margin: 0 }}>
          Generating Step 0{stepNumber} Output
        </Title>
        <Text type="secondary">
          Your request is running. You can stay on this page or open the latest output.
        </Text>
        <Progress percent={72} status="active" />
        {onOpenOutput ? (
          <Button type="primary" onClick={onOpenOutput}>
            Open Output Page
          </Button>
        ) : null}
      </Space>
    </Card>
  );
};

export default StepGenerating;
